import { err } from "../result.js";
import { modelReferencesType, visibleDeclsForTarget, } from "../model/types.js";
import { ModelBuilder, record, union, alias } from "../model/builder.js";
import { isList, isMap, isOption, mapBuiltinName, parseTypeRef, resolveFieldTypes, splitGenericArgs, } from "./parse-typeref.js";
import { orderBySource, parseFields, scanAll } from "./scan-decls.js";
// ── Type mapping ──
const TD_TO_PY = {
    Bool: "bool",
    Int: "int",
    Float: "float",
    String: "str",
    Bytes: "bytes",
    Unit: "None",
    List: "list",
    Map: "dict",
    Option: "Optional",
    Any: "Any",
    DateTime: "datetime.datetime",
    Uuid: "uuid.UUID",
    Decimal: "decimal.Decimal",
};
const PY_TO_TD = {
    bool: "Bool",
    int: "Int",
    float: "Float",
    str: "String",
    bytes: "Bytes",
    None: "Unit",
    list: "List",
    dict: "Map",
    Optional: "Option",
    List: "List",
    Dict: "Map",
    Set: "List",
    Tuple: "List",
    "datetime.datetime": "DateTime",
    datetime: "DateTime",
    "uuid.UUID": "Uuid",
    UUID: "Uuid",
    "decimal.Decimal": "Decimal",
    Decimal: "Decimal",
};
// [MODEL-SCALARS] stdlib module imported per scalar when the model uses it.
const PY_SCALAR_MODULES = [
    ["DateTime", "datetime"],
    ["Uuid", "uuid"],
    ["Decimal", "decimal"],
];
const scalarImportLines = (decls) => PY_SCALAR_MODULES.filter(([scalar]) => modelReferencesType(decls, scalar)).map(([, mod]) => `import ${mod}`);
// ── From Python ──
const CLASS_RE = /@dataclass\s*\n\s*class\s+(\w+)(?:\(([^)]*)\))?\s*:\s*\n((?:\s+\w+\s*:.+\n?)*)/g;
const ENUM_RE = /class\s+(\w+)\((?:str,\s*)?Enum\)\s*:\s*\n((?:[ \t]+\w+\s*=.+\n?)*)/g;
const TYPED_DICT_RE = /class\s+(\w+)\(TypedDict\)\s*:\s*\n((?:\s+\w+\s*:.+\n?)*)/g;
const PY_FIELD_RE = /(\w+)\s*:\s*(.+)/;
// Union alias line emitted by toPython for struct-variant unions:
//   Option = OptionSome | "None"
// RHS is a `|`-separated list of variant-class names and/or string literals
// (for bare variants). Matches a whole line.
const UNION_ALIAS_RE = /^(\w+)\s*=\s*([\w"'|\s]+)\s*$/gm;
// Plain alias: `Email = str` or `IdList = list[str]`. Same LHS as union-alias
// but RHS lacks `|`. Parsed in a second pass so we can tell them apart.
const PLAIN_ALIAS_RE = /^(\w+)\s*=\s*(\w[\w\s[\],.]*)$/gm;
// Generic parameters: `class Foo(Generic[T])`, `class Foo(Generic[T, U])`,
// or `class Foo(Bar, Generic[T])`. We extract the bracketed list.
const GENERIC_BASE_RE = /Generic\[([^\]]+)\]/;
const mapPyType = (t) => {
    const cleaned = t.trim().replace(/\s*#.*$/, "");
    const normalized = cleaned.replace(/\[/g, "<").replace(/\]/g, ">");
    const angleBracket = normalized.indexOf("<");
    const baseName = angleBracket === -1 ? normalized : normalized.slice(0, angleBracket);
    const mapped = PY_TO_TD[baseName] ?? baseName;
    if (angleBracket === -1) {
        return mapped;
    }
    const inner = normalized.slice(angleBracket + 1, normalized.lastIndexOf(">"));
    const args = splitGenericArgs(inner).map(mapPyType);
    return `${mapped}<${args.join(", ")}>`;
};
const parsePyFields = (body) => parseFields(body, {
    separator: "\n",
    commentPrefix: "#",
    fieldRe: PY_FIELD_RE,
    mapType: (type) => mapPyType(type.replace(/\s*=.*$/, "").trim()),
});
const extractGenericsFromBases = (bases) => {
    if (bases === undefined) {
        return [];
    }
    const gm = GENERIC_BASE_RE.exec(bases);
    if (gm?.[1] === undefined) {
        return [];
    }
    return gm[1]
        .split(",")
        .map((g) => g.trim())
        .filter((g) => g.length > 0);
};
const fromPython = (source) => {
    const builder = new ModelBuilder();
    let found = false;
    const pending = [
        ...scanAll(CLASS_RE, source, (m) => {
            const [, name, bases, body] = m;
            return name === undefined || body === undefined ? null : { kind: "class", name, bases, body, offset: m.index };
        }),
        ...scanAll(TYPED_DICT_RE, source, (m) => {
            const [, name, body] = m;
            return name === undefined || body === undefined ? null : { kind: "typed-dict", name, body, offset: m.index };
        }),
        ...scanAll(ENUM_RE, source, (m) => {
            const [, name, body] = m;
            return name === undefined || body === undefined ? null : { kind: "enum", name, body, offset: m.index };
        }),
    ];
    const classNames = new Set(pending.filter((p) => p.kind === "class" || p.kind === "typed-dict").map((p) => p.name));
    const enumNames = new Set(pending.filter((p) => p.kind === "enum").map((p) => p.name));
    // Scan for top-level `=` lines. Distinguish:
    //   * Union-alias (`Foo = A | B | "C"`): has `|`.
    //   * Plain alias (`Email = str`): no `|`, and the LHS name isn't already a
    //     class/enum we've captured (to avoid re-capturing something like
    //     `ExampleValue = "x"` inside an enum body — enum bodies are multi-line
    //     but the regex is anchored to line start so we still need to filter).
    pending.push(...scanAll(UNION_ALIAS_RE, source, (m) => {
        const [, name, rhs] = m;
        return name === undefined ||
            rhs === undefined ||
            !rhs.includes("|") ||
            classNames.has(name) ||
            enumNames.has(name)
            ? null
            : { kind: "union-alias", name, rhs, offset: m.index };
    }));
    const unionAliasNames = new Set(pending.filter((p) => p.kind === "union-alias").map((p) => p.name));
    pending.push(...scanAll(PLAIN_ALIAS_RE, source, (m) => {
        const [, name, rhs] = m;
        if (name === undefined || rhs === undefined) {
            return null;
        }
        if (classNames.has(name) || enumNames.has(name) || unionAliasNames.has(name)) {
            return null;
        }
        // Skip import lines, assignments like `x = 42`, TypeVar declarations,
        // etc. — keep only things that look like a type alias.
        const trimmed = rhs.trim();
        if (trimmed.length === 0 || /^[0-9"']/.test(trimmed) || trimmed.startsWith("TypeVar(")) {
            return null;
        }
        return { kind: "plain-alias", name, rhs: trimmed, offset: m.index };
    }));
    const ordered = orderBySource(pending);
    const variantClassToUnion = new Map();
    const unionVariants = new Map();
    for (const p of ordered) {
        if (p.kind !== "union-alias") {
            continue;
        }
        const parts = p.rhs
            .split("|")
            .map((s) => s.trim())
            .filter((s) => s.length > 0);
        const variants = [];
        for (const part of parts) {
            const literalMatch = /^["'](.+)["']$/.exec(part);
            if (literalMatch?.[1] !== undefined) {
                variants.push({ name: literalMatch[1], className: null });
                continue;
            }
            const className = part;
            // Strip the union-name prefix from the class-name if present (e.g.
            // `OptionSome` -> `Some` under union `Option`).
            const variantName = className.startsWith(p.name) ? className.slice(p.name.length) : className;
            variants.push({ name: variantName, className });
            variantClassToUnion.set(className, { unionName: p.name, variantName });
        }
        unionVariants.set(p.name, variants);
    }
    for (const p of ordered) {
        if (p.kind === "class" || p.kind === "typed-dict") {
            // If this class is a folded union variant, skip — it'll be emitted
            // under the union's variant list below.
            if (variantClassToUnion.has(p.name)) {
                continue;
            }
            found = true;
            const fields = resolveFieldTypes(parsePyFields(p.body));
            const generics = p.kind === "class" ? extractGenericsFromBases(p.bases) : [];
            builder.add(record(p.name, fields, generics));
            continue;
        }
        if (p.kind === "enum") {
            found = true;
            const variants = p.body
                .split("\n")
                .map((l) => l.trim())
                .filter((l) => l.length > 0 && !l.startsWith("#"))
                .map((l) => {
                const [variantName] = l.split("=");
                return { name: (variantName ?? l).trim(), fields: [] };
            });
            builder.add(union(p.name, variants));
            continue;
        }
        if (p.kind === "union-alias") {
            found = true;
            const variants = unionVariants.get(p.name) ?? [];
            const builtVariants = variants.map((v) => {
                if (v.className === null) {
                    return { name: v.name, fields: [] };
                }
                const cls = pending.find((x) => (x.kind === "class" || x.kind === "typed-dict") && x.name === v.className);
                if (cls === undefined || (cls.kind !== "class" && cls.kind !== "typed-dict")) {
                    return { name: v.name, fields: [] };
                }
                const fields = resolveFieldTypes(parsePyFields(cls.body));
                return { name: v.name, fields };
            });
            // Look for the first variant class's generics — generic unions (e.g.
            // Option[T]) emit their variants with the same generics.
            const firstCls = variants
                .map((v) => (v.className === null ? null : pending.find((x) => x.kind === "class" && x.name === v.className)))
                .find((x) => x !== null && x !== undefined);
            const generics = firstCls?.kind === "class" ? extractGenericsFromBases(firstCls.bases) : [];
            builder.add(union(p.name, builtVariants, generics));
            continue;
        }
        // plain-alias
        found = true;
        builder.add(alias(p.name, parseTypeRef(mapPyType(p.rhs))));
    }
    return found
        ? builder.build()
        : err([{ severity: "error", message: "No Python type definitions found", line: 0, col: 0, length: 0 }]);
};
// ── To Python ──
const mapTdToPyDataclass = (t) => {
    const name = mapBuiltinName(t, TD_TO_PY);
    return t.args.length === 0 ? name : `${name}[${t.args.map(mapTdToPyDataclass).join(", ")}]`;
};
const mapTdToPyPydantic = (t) => {
    if (isOption(t) && t.args.length === 1) {
        const inner = t.args[0];
        if (inner !== undefined) {
            return `${mapTdToPyPydantic(inner)} | None`;
        }
    }
    const name = mapBuiltinName(t, TD_TO_PY);
    return t.args.length === 0 ? name : `${name}[${t.args.map(mapTdToPyPydantic).join(", ")}]`;
};
const dataclassFieldSuffix = (t) => isOption(t)
    ? " = None"
    : isList(t)
        ? " = field(default_factory=list)"
        : isMap(t)
            ? " = field(default_factory=dict)"
            : "";
const pydanticFieldSuffix = (t) => isOption(t)
    ? " = None"
    : isList(t)
        ? " = Field(default_factory=list)"
        : isMap(t)
            ? " = Field(default_factory=dict)"
            : "";
const needsDataclassField = (decls) => decls.some((d) => (d.kind === "record" && d.fields.some((f) => isList(f.type) || isMap(f.type))) ||
    (d.kind === "union" && d.variants.some((v) => v.fields.some((f) => isList(f.type) || isMap(f.type)))));
const hasBareEnum = (decls) => decls.some((d) => d.kind === "union" && d.variants.every((v) => v.fields.length === 0));
const hasOption = (decls) => decls.some((d) => (d.kind === "record" && d.fields.some((f) => isOption(f.type))) ||
    (d.kind === "union" && d.variants.some((v) => v.fields.some((f) => isOption(f.type)))) ||
    (d.kind === "alias" && isOption(d.target)));
const hasGenerics = (decls) => decls.some((d) => d.generics.length > 0);
const buildDataclassImports = (decls) => {
    const lines = ["from __future__ import annotations", ...scalarImportLines(decls)];
    const dataclassImports = ["dataclass"];
    if (needsDataclassField(decls)) {
        dataclassImports.push("field");
    }
    lines.push(`from dataclasses import ${dataclassImports.join(", ")}`);
    if (hasBareEnum(decls)) {
        lines.push("from enum import Enum");
    }
    const typingNames = [];
    if (hasOption(decls)) {
        typingNames.push("Optional");
    }
    if (modelReferencesType(decls, "Any")) {
        typingNames.push("Any");
    }
    if (hasGenerics(decls)) {
        typingNames.push("Generic", "TypeVar");
    }
    if (typingNames.length > 0) {
        lines.push(`from typing import ${typingNames.join(", ")}`);
    }
    // Declare the TypeVars used across the model so `Generic[T]` resolves.
    const typeVars = new Set();
    for (const d of decls) {
        for (const g of d.generics) {
            typeVars.add(g);
        }
    }
    if (typeVars.size > 0) {
        for (const tv of [...typeVars].sort()) {
            lines.push(`${tv} = TypeVar("${tv}")`);
        }
    }
    lines.push("");
    return lines;
};
const buildPydanticImports = (decls) => {
    const lines = ["from __future__ import annotations", ...scalarImportLines(decls), "from pydantic import BaseModel"];
    const hasCollections = decls.some((d) => (d.kind === "record" && d.fields.some((f) => isList(f.type) || isMap(f.type))) ||
        (d.kind === "union" && d.variants.some((v) => v.fields.some((f) => isList(f.type) || isMap(f.type)))));
    if (hasCollections) {
        lines.push("from pydantic import Field");
    }
    if (hasBareEnum(decls)) {
        lines.push("from enum import Enum");
    }
    if (modelReferencesType(decls, "Any")) {
        lines.push("from typing import Any");
    }
    lines.push("");
    return lines;
};
const genericBase = (generics) => (generics.length === 0 ? "" : `(Generic[${generics.join(", ")}])`);
const emitDataclassRecord = (name, fields, generics = []) => {
    const lines = ["@dataclass", `class ${name}${genericBase(generics)}:`];
    if (fields.length === 0) {
        lines.push("    pass");
        return lines;
    }
    for (const f of fields) {
        lines.push(`    ${f.name}: ${mapTdToPyDataclass(f.type)}${dataclassFieldSuffix(f.type)}`);
    }
    return lines;
};
const emitPydanticRecord = (name, fields, generics = []) => {
    const bases = generics.length > 0 ? `BaseModel, Generic[${generics.join(", ")}]` : "BaseModel";
    const lines = [`class ${name}(${bases}):`];
    if (fields.length === 0) {
        lines.push("    pass");
        return lines;
    }
    for (const f of fields) {
        lines.push(`    ${f.name}: ${mapTdToPyPydantic(f.type)}${pydanticFieldSuffix(f.type)}`);
    }
    return lines;
};
const variantClassName = (unionName, variantName) => `${unionName}${variantName}`;
const emitBareEnum = (name, variants) => {
    const lines = [`class ${name}(str, Enum):`];
    for (const v of variants) {
        lines.push(`    ${v.name} = "${v.name.toLowerCase()}"`);
    }
    return lines;
};
const toPython = (model, opts) => {
    const pydantic = opts?.style === "pydantic";
    const decls = visibleDeclsForTarget(model.decls, "python");
    const lines = pydantic ? buildPydanticImports(decls) : buildDataclassImports(decls);
    const emitRecord = pydantic ? emitPydanticRecord : emitDataclassRecord;
    for (const d of decls) {
        if (d.kind === "record") {
            lines.push(...emitRecord(d.name, d.fields, d.generics), "");
        }
        else if (d.kind === "union") {
            const allEmpty = d.variants.every((v) => v.fields.length === 0);
            if (allEmpty) {
                lines.push(...emitBareEnum(d.name, d.variants), "");
                continue;
            }
            for (const v of d.variants.filter((x) => x.fields.length > 0)) {
                lines.push(...emitRecord(variantClassName(d.name, v.name), v.fields, d.generics), "");
            }
            const variantTypes = d.variants.map((v) => v.fields.length > 0 ? variantClassName(d.name, v.name) : `"${v.name}"`);
            lines.push(`${d.name} = ${variantTypes.join(" | ")}`, "");
        }
        else {
            const mapper = pydantic ? mapTdToPyPydantic : mapTdToPyDataclass;
            lines.push(`${d.name} = ${mapper(d.target)}`, "");
        }
    }
    return lines.join("\n");
};
export const python = {
    language: "python",
    fromSource: fromPython,
    toSource: (model, opts) => toPython(model, opts),
};
//# sourceMappingURL=python.js.map